import {
    IIdentityProvider,
    IMessage,
    IMessageProvider,
    IProgressProvider,
    MessageType
} from "../PA.Core/PA.Core.IF.Basic";
import { BehaviorSubject } from "rxjs";
import {
    ApplicationModuleStartupOptions,
    IApplicationModuleSession,
    IApplicationSession,
    INavigationItem,
    IPlatformViewport, IPlatformViewportRenderer
} from "./PA.UIM.Core.IF.Platform";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IPolicyCollection } from "../PA.Core/PA.Core.IF.Policy";
import { NoteBO } from "../PA.MD.BusinessObjects/NoteBO";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { IApplicationModule } from "./PA.UIM.Core.IF.Framework";
import { ASync } from "../PA.Core/Basic/ASync";
import { IHaveSecurity, instanceOfIHaveSecurity } from "../PA.Core/PA.Core.IF.Aspects";
import { PolicyBOis } from "../PA.Core/Security/PolicyBOis";
import { PolicyHasRole } from "../PA.Core/Security/PolicyHasRole";
import * as _ from "lodash"
import { PolicyCollection } from "../PA.Core/Security/PolicyCollection";

import { ISearch, ISearchProfile, ISearchProvider, ISearchRendererOptions } from "./PA.UIM.Core.IF.Search";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { PlatformSession } from "./PlatformSession";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { SearchUtils, StandardSearchResults } from "../PA.UIM.Core/SearchUtils";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { UIFrameworkService } from "../PA.UIFramework.Services/UIFrameworkService";
import { IUsageDetails } from "../PA.UIFramework.Services/PA.UIFramework.IF.Services";
import { IRefCounter } from "../PA.Concurrency.Core/PA.Concurrency.IF.RefCounter";


export interface IApplicationModuleSessionCreateOptions {
    dbContext?: PAdbContext,
    BusinessObjectCreateNewOnSessionCreateSkip?: boolean,
    bIsSearchSession?: boolean
}

export class ApplicationModuleSession
    implements IApplicationModuleSession {

    private _SessionId: number
    private _Identity: IIdentityProvider
    private _ApplicationSession: IApplicationSession
    private _ApplicationModule: IApplicationModule
    private _PlatformViewport: IPlatformViewport

    private _dbContext: PAdbContext
    private _BusinessObject: IBusinessObject


    private _ActionProgress: IProgressProvider
    private _ActionMessages: MessageProvider
    private _OnActionComplete: BehaviorSubject<{ Session: IApplicationModuleSession, Action: string }>;
    private _SessionChanged: BehaviorSubject<IApplicationModuleSession>

    private _ActionIsRunning: boolean;  // Can't use promise as indicator
    private _OnActionCompletePromise: Promise<IApplicationModuleSession>;
    private _ActionStateChanged: BehaviorSubject<IApplicationModuleSession>;

    private _PolicyCollection: IPolicyCollection

    private _bIsSearchSession: boolean
    private _bIsClosed: boolean
    private _bCreateNewVisible: boolean;
    private _bCopyVisible: boolean;
    private _bSaveVisible: boolean;
    private _bDeleteVisible: boolean
    private _bSearchVisible: boolean;
    private _bCloseVisible: boolean;
    private _bCreateNoteVisible: boolean;
    private _bReloadBOVisible: boolean;
    private _bIdDialogVisible: boolean;
    private _bRevertChangesVisible: boolean;
    private _bFileListVisible: boolean;

    private _bNavigationVisible: boolean;
    private _navigationItems: INavigationItem[]
    private _currentNavigationItem: INavigationItem
    private _bCacheBO: boolean


    private _bShowQuickSearch: boolean;
    private _sQuickSearchAdditionalApps: string[];
    private _sQuickSearchPlacementText: string

    private _loadOption: any

    constructor(platformViewport: IPlatformViewport,
                appSession: IApplicationSession,
                appModule: IApplicationModule,
                options: IApplicationModuleSessionCreateOptions) {
        const opts = options || {}
        const executable = appModule.executable

        this._SessionId = PlatformSession.getNextSessionId()
        this._Identity = new IdentityProvider({ id: this._SessionId, id_ui: this._SessionId, name: appModule.name })
        this._PlatformViewport = platformViewport
        this._ApplicationSession = appSession
        this._ApplicationModule = appModule
        if (!_.isEmpty(opts.dbContext))
            this._dbContext = new PAdbContext()
        else
            this._dbContext = opts.dbContext


        const bHasEditRights = Object.values(executable.editRoles).includes(true)

        this._BusinessObject = undefined
        this._SessionChanged = new BehaviorSubject<IApplicationModuleSession>(this)
        this._ActionProgress = new ProgressProvider()
        this._ActionMessages = new MessageProvider()
        this._ActionIsRunning = false
        this._OnActionCompletePromise = undefined
        this._OnActionComplete = new BehaviorSubject<{ Session: IApplicationModuleSession, Action: string }>(undefined)
        this._bIsClosed = false

        this._bIsSearchSession = !!opts.bIsSearchSession

        this._bCreateNewVisible = (executable.tbCreateNewShowInd && bHasEditRights)
        this._bCopyVisible = (executable.tbCopyShowInd && bHasEditRights)
        this._bSaveVisible = (executable.tbSaveShowInd && bHasEditRights)
        this._bDeleteVisible = (executable.tbDeleteShowInd && bHasEditRights)
        this._bSearchVisible = executable.tbSearchShowInd
        this._bCloseVisible = executable.tbCloseShowInd
        this._bRevertChangesVisible = false
        this._bFileListVisible = executable.tbFileListShowInd
        this._bIdDialogVisible = executable.tbIdDialogShowInd

        this._bNavigationVisible = false // until we show this again
        this._navigationItems = []
        this._currentNavigationItem = undefined

        this._bCreateNoteVisible = executable.tbCreateNoteShowInd
        this._bReloadBOVisible = executable.tbReloadShowInd
        this._bShowQuickSearch = executable.tbSearchShowInd
        this._sQuickSearchAdditionalApps = []
        this._sQuickSearchPlacementText = "Quick Search"
        this._bCacheBO = executable.bCacheBO
        this._loadOption = opts


        this._ActionStateChanged = new BehaviorSubject<IApplicationModuleSession>(this)

        this._Identity.IdentityChanged.subscribe(this._evtOnIdentityChanged.bind(this))

        if (executable.boCreateNewOnSessionCreate
            && (!opts.BusinessObjectCreateNewOnSessionCreateSkip)) {
            this.LoadNew({})
        }
    }

    get SessionId(): number {
        return this._SessionId
    }

    get Identity(): IIdentityProvider {
        return this._Identity
    }

    get ApplicationModule(): IApplicationModule {
        return this._ApplicationModule
    }

    get PlatformViewport(): IPlatformViewport {
        return this._PlatformViewport
    }

    get ApplicationSession(): IApplicationSession {
        return this._ApplicationSession
    }

    get dbContext(): PAdbContext {
        return this._dbContext
    }

    get BusinessObject(): IBusinessObject {
        return this._BusinessObject
    }

    get ActionMessages(): IMessageProvider {
        return this._ActionMessages
    }

    get ActionProgress(): IProgressProvider {
        return this._ActionProgress
    }

    get OnActionComplete(): BehaviorSubject<{ Session: IApplicationModuleSession; Action: string }> {
        return this._OnActionComplete
    }

    get ActionStateChanged(): BehaviorSubject<IApplicationModuleSession> {
        return this._ActionStateChanged
    }

    get SessionChanged(): BehaviorSubject<IApplicationModuleSession> {
        return this._SessionChanged
    }

    get PolicyCollection(): IPolicyCollection {
        return this._PolicyCollection
    }


    RunStartup(options?: ApplicationModuleStartupOptions): void {
        const self = this
        let bStartupPolicy = true
        if (_.isEmpty(options))
            bStartupPolicy = true
        else
            bStartupPolicy = !options.bIgnoreStartupPolicy

        if (self.ApplicationModule.placementPolicy === 'PaneContainer')
            return

        if ((self.ApplicationModule.startupPolicy === 'AutoStartSearch')
            && (bStartupPolicy)) {
            self.Search({ autoSearch: true })
            return
        }

        if (self.ApplicationModule.executableParams?.autoLoad && !options?.bIgnoreAutoLoad) {
            (async () => {
                // Execute Search and open multiple
                if (_.isEmpty(self.ApplicationModule.executable.searchClass))
                    return

                const searchOpts: ISearchRendererOptions = self.getSearchOptions({})
                const stdSearchResults
                    = await SearchUtils.getStandardSearchResults(self.ApplicationModule.executable.searchClass, searchOpts.filterCondition || {}, {})

                await self.setNavigationList(stdSearchResults, options)

            })();

        }

    }

    isObject(id: number | string): boolean {
        if (_.isEmpty(this._BusinessObject))
            return false;

        if (!(this._BusinessObject.bIsLoaded))
            return false;

        return (this._BusinessObject.Identity.id == id)
    }

    isBusinessObject(aBO: IBusinessObject): boolean {
        if (_.isEmpty(this._BusinessObject))
            return false;

        if (!(this._BusinessObject.bIsLoaded))
            return false;

        return (this._BusinessObject == aBO)
    }

    logUsage(usage: string, StartDt?: Date, EndDt?: Date, ErrorInd?: boolean, pBO?: IBusinessObject, pId?: number): Promise<void> {
        const self = this
        const fs = new UIFrameworkService()
        const usageDetails: IUsageDetails
            = {
            Usage: usage,
            wbUIAppModuleId: self.ApplicationModule.wb_id,
            wbUIAppId: self.ApplicationSession.Application.wb_id,
            ErrorInd: !!ErrorInd,
            StartDtTs: StartDt,
            EndDtTs: EndDt
        }

        const aBO = pBO || self._BusinessObject
        if (!_.isEmpty(aBO?.Identity)) {
            if (+aBO.Identity.id > 0) {
                usageDetails.BO_wb_id = _.toNumber(aBO.Identity.id)
                usageDetails.BO_ui_id = aBO.Identity.id_ui + ''
                usageDetails.BO_name = aBO.Identity.name
            }
        }
        if (!_.isEmpty(pId)) {
            usageDetails.BO_wb_id = pId
        }
        fs.LogUsageSync(usageDetails)
        return fs.SaveUsage()
    }


    async LoadNew(options?: any): Promise<IApplicationModuleSession> {
        const self = this;
        const selfany: any = this;

        const OperationStart = new Date()

        self._ActionMessages.clearMessages()
        if (self._ActionIsRunning)
            throw new Error("Session Action already running.")

        self._ActionIsRunning = true

        const returnValue = this._OnActionCompletePromise = (async (): Promise<IApplicationModuleSession> => {

            let aNewBO: IBusinessObject = undefined

            try {
                let bLoadContinue = true
                if ('beforeLoadNew' in selfany)
                    bLoadContinue = await selfany.beforeLoadNew(self)

                if (!bLoadContinue) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }

                const bConfirm = await self._unloadBOCheck()
                if (!bConfirm) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }


                aNewBO = self.createBO(options)

                if (_.isEmpty(aNewBO)) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }


                const executable = self.ApplicationModule.executable

                let wizardOptions: any = {}
                let createOptionsDefault: any
                createOptionsDefault = _.merge(self.ApplicationModule.executableParams.createNewOptions || {}, executable.boCreateNewOptions || {})
                if (!_.isEmpty(executable.boCreateNewWizard)) {
                    let wizard: any = undefined
                    if (!_.isEmpty(createOptionsDefault) && 'settings' in createOptionsDefault) {
                        // @ts-expect-error "Ignore"
                        wizard = new executable.boCreateNewWizard.wizardFunction({ createOptions: createOptionsDefault })
                    } else {
                        // @ts-expect-error "Ignore"
                        wizard = new executable.boCreateNewWizard.wizardFunction()
                    }
                    wizardOptions = await wizard.open()
                    if (_.isEmpty(wizardOptions)) {
                        return self
                    }

                }

                const createOptions: any = {}

                _.defaults(createOptions, options || {})
                _.defaults(createOptions, self._ApplicationSession.Application.renderOptions)
                _.defaults(createOptions, createOptionsDefault)
                _.defaults(createOptions, wizardOptions)

                // TODO:  why is this here?  what does this do ?
                if ('appSession' in executable.boCreateNewOptions) {
                    createOptions['appSession'] = self._ApplicationSession
                }

                aNewBO.LoadProgressProvider.addListenerUtilComplete(self._ActionProgress)

                await aNewBO.LoadNew(createOptions)
                aNewBO.LoadProgressProvider.removeListener(self._ActionProgress)

                if (aNewBO.bIsLoaded) {
                    if (!_.isEmpty(executable.boAfterCreateNewWizard)) {
                        // @ts-expect-error "Ignore"
                        const wizard = new executable.boAfterCreateNewWizard.wizardFunction(aNewBO)
                        aNewBO = await wizard.open()
                        if (_.isEmpty(aNewBO)) {
                            await self._replaceBO(aNewBO, false)
                            self._ActionIsRunning = false
                            self._OnActionCompletePromise = undefined
                            return self;
                        }
                    }
                    await self._replaceBO(aNewBO, false)
                    self._bNavigationVisible = true
                    self.logUsage("LoadNew", OperationStart, new Date(), false, aNewBO)
                } else {
                    const errorTxt = "Load New Failed"
                    const loadErrorMsg: IMessage = {
                        code: "loadNewFailed",
                        title: "Load New Failed",
                        subtitle: "",
                        description: JSON.stringify(aNewBO.LoadProgressProvider.process_exception, null, '\t'),
                        type: MessageType.Error
                    }
                    self._ActionMessages.addMessage(loadErrorMsg);
                    self._ActionProgress.exception(errorTxt);
                    throw new Error(aNewBO.LoadProgressProvider.process_exception)
                }

            } catch (err) {
                self.logUsage("LoadNew", OperationStart, new Date(), true, aNewBO)

                throw new Error(err)

            } finally {
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self._notifyActionStatesCompleted("LoadNew")
                self._notifySessionChanged()
            }

        })();

        self._notifyActionStatesChanged()
        return returnValue
    }

    async LoadId(id: number | string, options?: any): Promise<IApplicationModuleSession> {
        const self = this;
        const selfany: any = this;

        const OperationStart = new Date()

        self._ActionMessages.clearMessages()
        if (self._ActionIsRunning)
            throw new Error("Session Action already running.")

        self._ActionIsRunning = true
        const returnValue = this._OnActionCompletePromise = (async (): Promise<IApplicationModuleSession> => {

            const aNewBO: IBusinessObject = undefined

            try {
                let bLoadContinue = true

                if ('beforeLoad' in selfany)
                    bLoadContinue = await selfany.beforeLoad(self, id)

                if (!bLoadContinue) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }

                const bConfirm = await self._unloadBOCheck()
                if (!bConfirm) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }

                await self._loadId(id, options)
                self._bNavigationVisible = true
                self.logUsage("Load", OperationStart, new Date(), false, aNewBO)
            } catch (err) {
                self.logUsage("Load", OperationStart, new Date(), true, aNewBO)
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self._notifyActionStatesCompleted("Load")
                self._notifySessionChanged()
                throw err
            } finally {
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self._notifyActionStatesCompleted("Load")
                self._notifySessionChanged()
            }

        })();


        //-- TODO Why was this here
        // self.PlatformViewport.Renderer.renderSession(self)

        self._notifyActionStatesChanged()
        return returnValue

    }

    async SetBO(aBO: IBusinessObject): Promise<IApplicationModuleSession> {

        const self = this;
        const selfany: any = this;

        const OperationStart = new Date()

        if (self.bIsSearchSession) {
            self._BusinessObject = aBO
            return
        }

        self._ActionMessages.clearMessages()
        if (self._ActionIsRunning)
            throw new Error("Session Action already running.")


        if (_.isEmpty(aBO))
            throw new Error("Load BO is undefined")


        if ((!(aBO.bIsLoaded)) && _.isEmpty(aBO.OnLoadComplete))
            throw new Error("BO isn't loaded.")

        if (!aBO.bIsLoaded)
            await aBO.OnLoadComplete

        self._ActionIsRunning = true
        const returnValue = this._OnActionCompletePromise = (async (): Promise<IApplicationModuleSession> => {
            try {

                let bSetContinue = true

                if ('beforeSetBO' in selfany)
                    bSetContinue = await selfany.beforeSetBO(self, aBO)

                if (!bSetContinue) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }

                const bConfirm = await self._unloadBOCheck()
                if (!bConfirm) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }

                if (aBO.bIsLoaded) {
                    await self._replaceBO(aBO, true)
                    self._bNavigationVisible = self.ApplicationModule.executable.tbSearchShowInd
                    self.logUsage("SetBO", OperationStart, new Date(), false, aBO)
                } else {
                    // Do nothing, it's been
                }

            } catch (err) {
                self.logUsage("SetBO", OperationStart, new Date(), true, aBO)
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self._notifyActionStatesCompleted("Load")
                self._notifySessionChanged()
                throw err
            } finally {
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self._notifyActionStatesCompleted("SetBO")
                self._notifySessionChanged()
            }

        })()

        self._notifyActionStatesChanged()
        return returnValue
    }

    get bIsObjectLoaded(): boolean {
        return !_.isEmpty(this._BusinessObject)
    }

    get bCreateNewVisible(): boolean {
        return this._bCreateNewVisible
    }

    get bCreateNewEnabled(): boolean {
        return this._bCreateNewVisible &&
            (!this._ActionIsRunning) &&
            (!this.bIsClosed)
    }

    async CreateNew(): Promise<IApplicationModuleSession> {
        const self = this

        const OperationStart = new Date()
        try {
            await self.LoadNew(self.ApplicationModule.executable.boLoadOptions || {})
            self.logUsage("CreateNew", OperationStart, new Date(), false, self.BusinessObject)
        } catch (err) {
            self.logUsage("CreateNew", OperationStart, new Date(), true, self.BusinessObject)
        }
        return self
    }

    get bIsSearchSession(): boolean {
        return this._bIsSearchSession
    }

    get bCloseVisible(): boolean {
        return this._bCloseVisible
    }

    get bCloseEnabled(): boolean {
        return this._bCloseVisible && (!this._ActionIsRunning)
    }

    async Close(options?: { bSkipSaveCheck?: boolean }): Promise<boolean> {
        const self = this
        const selfany: any = this

        const opts = options || {}

        const OperationStart = new Date()
        const returnValue = (async (): Promise<boolean> => {
            try {

                if (!_.isUndefined(self._OnActionCompletePromise))
                    await self._OnActionCompletePromise

                if (self._ActionIsRunning)
                    throw new Error("Can't close when action is running")

                let bContinue = true
                if ('beforeClose' in selfany)
                    bContinue = await selfany.beforeClose(self)


                if (!bContinue)
                    return false;

                let bConfirm: boolean
                if (opts.bSkipSaveCheck)
                    bConfirm = true
                else
                    bConfirm = await self._unloadBOCheck()

                if (!bConfirm)
                    return false

                for (const anItem of self.NavigationItems) {
                    let bo = undefined
                    if (!_.isEmpty(anItem.bo)) {
                        bo = anItem.bo
                    } else
                        bo = self.BusinessObject

                    // @ts-expect-error "Ignore"
                    if (!_.isEmpty(bo) && !_.isEmpty(bo.RefCounter)) {
                        // @ts-expect-error "Ignore"
                        await bo.RefCounter.releaseReference()
                    }
                    // @ts-expect-error "Ignore"
                    if (!_.isEmpty(bo) && !_.isEmpty(bo.EventCollection)) {
                        // @ts-expect-error "Ignore"
                        await bo.EventCollection.releaseReference()
                    }
                }

                self._bIsClosed = true
                self.ApplicationSession.PruneSession(self)
                self.logUsage("Close", OperationStart, new Date(), false)
                self._ActionMessages.addTextMessages("Close", MessageType.Information, "Close")
                self._notifyActionStatesChanged()
                self._ActionMessages.clearMessages()
                self.PlatformViewport.Renderer.destroySession(self)
                return true


            } catch (err) {
                self.logUsage("Close", OperationStart, new Date(), true)
                return false
            }
        })();

        return returnValue
    }


    get bIsClosed(): boolean {
        return this._bIsClosed
    }

    get bSaveVisible(): boolean {
        return this._bSaveVisible
    }

    get bSaveEnabled(): boolean {
        return this.bSaveVisible &&
            (!_.isEmpty(this._BusinessObject)) &&
            (!this._ActionIsRunning)
    }

    async Save(): Promise<IApplicationModuleSession> {
        const self = this
        const selfany: any = this

        const OperationStart = new Date()

        if (!(self.bSaveEnabled))
            return self

        if (self._ActionIsRunning)
            throw new Error("Session Action already running.")

        self._ActionIsRunning = true
        const returnValue = this._OnActionCompletePromise = (async (): Promise<IApplicationModuleSession> => {

            try {
                let bContinue = true
                if ('beforeSave' in self)
                    bContinue = await selfany.beforeSave(self)

                if (!bContinue) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self
                }

                await self.BusinessObject.Save()

                //switch the objects bExists property to true
                self._currentNavigationItem.bExists = true
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self._notifyActionStatesCompleted("Save")
                self.logUsage("Save", OperationStart, new Date(), false)
            } catch (err) {
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self.logUsage("Save", OperationStart, new Date(), true)
                throw err
            }

        })();

        self._notifyActionStatesChanged()
        return returnValue
    }


    get bDeleteVisible(): boolean {
        return this._bDeleteVisible
    }

    get bDeleteEnabled(): boolean {
        return this.bDeleteVisible &&
            (!_.isEmpty(this._BusinessObject)) &&
            (!this._ActionIsRunning)
    }

    async Delete(): Promise<IApplicationModuleSession> {
        const self = this
        const selfany: any = this

        const OperationStart = new Date()

        const deleteBO = self._BusinessObject
        const id = deleteBO.Identity.id
        const bDeleted: boolean = false

        if (!(self.bDeleteEnabled))
            return self

        self._ActionMessages.clearMessages()
        if (this._ActionIsRunning)
            throw new Error("Session Action already running.")

        self._ActionIsRunning = true
        const returnValue = this._OnActionCompletePromise = (async (): Promise<IApplicationModuleSession> => {

            let bCloseOnCompete = false
            try {
                let bContinue = true
                if ('beforeDelete' in self)
                    bContinue = await selfany.beforeDelete(self)

                if (!bContinue) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self
                }

                const boNameId = `${deleteBO?.Identity?.name} (${deleteBO?.Identity?.id_ui})`
                const bConfirm = await self.PlatformViewport.Renderer.messageBoxRenderer.Confirm("Are you sure you want to delete?", { title: boNameId })

                if (!bConfirm) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self
                }

                if (!_.isFunction(deleteBO.Delete)) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._ActionMessages.addTextMessages("NoDeleteFunction", MessageType.Error, "Deleting Error:No delete function available for this module")
                    throw new Error("No delete function available for this module")
                }

                await deleteBO.Delete()

                if (!deleteBO.bIsDeleted) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self
                }
                self._BusinessObject = undefined

                let moveToBO: IBusinessObject = undefined
                _.remove(self._navigationItems, anItem => anItem.id == id)
                if (self._navigationItems.length) {
                    this._currentNavigationItem = _.first(self._navigationItems)

                    //if the item hasn't been saved yet - skip it/ delete from the navItems
                    if (this._currentNavigationItem.bExists) {
                        moveToBO = await self._loadId(this._currentNavigationItem.id)
                        await self._replaceBO(moveToBO, true)
                    } else {
                        _.remove(self._navigationItems, anItem => anItem.id == this._currentNavigationItem.id)
                        this._currentNavigationItem = undefined
                    }

                } else {
                    this._currentNavigationItem = undefined
                    bCloseOnCompete = true
                }

                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self._notifyActionStatesCompleted("Delete")
                self.logUsage("Delete", OperationStart, new Date(), false, deleteBO)
                self._notifySessionChanged()
                return self
            } catch (err) {
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self.logUsage("Delete", OperationStart, new Date(), true, deleteBO)
                throw err
            } finally {
                if (bCloseOnCompete)
                    await this.Close()
            }

        })();

        self._notifyActionStatesChanged()
        return returnValue
    }


    get bSearchVisible(): boolean {
        return this._bSearchVisible && !_.isEmpty(this.ApplicationModule.executable.searchClass) &&
            (this.ApplicationModule.placementPolicy !== 'PaneContainer')
    }

    get bSearchEnabled(): boolean {
        return this.bSearchVisible &&
            (!this._ActionIsRunning)
    }

    get bShowQuickSearch(): boolean {
        return this._bSearchVisible && !_.isEmpty(this.ApplicationModule.executable.searchClass)
    }

    get sQuickSearchAdditionalApps(): string[] {
        return this._sQuickSearchAdditionalApps
    }

    get sQuickSearchPlacementText(): string {
        return this._sQuickSearchPlacementText
    }

    private getSearchOptions(opts?: ISearchRendererOptions): ISearchRendererOptions {
        const self = this

        // Search options priority
        // 1. App Module Exec Filter Condition
        // 2. Options
        // 3. Exec Search Options
        let oAppModuleSearchCondition = {}
        if (!_.isEmpty(self.ApplicationModule.executableParams?.searchFilter))
            oAppModuleSearchCondition = { filterCondition: _.cloneDeep(self.ApplicationModule.executableParams.searchFilter) }
        const searchOpts: ISearchRendererOptions = {}

        _.defaults(searchOpts, oAppModuleSearchCondition, _.cloneDeep(opts || {}), self.ApplicationModule.executable.searchOptions)

        return searchOpts
    }

    async Search(opts?: ISearchRendererOptions): Promise<IApplicationModuleSession> {
        const self = this

        let currentBO: IBusinessObject


        if (!this.bSearchEnabled)
            return self

        if (self.BusinessObject)
            self.BusinessObject.ValidateMessageProvider.clearMessages()

        self._ActionMessages.clearMessages()

        if (self._ActionIsRunning)
            throw new Error("Session Action already running.")

        self._ActionIsRunning = true
        const returnValue = this._OnActionCompletePromise = (async (): Promise<IApplicationModuleSession> => {
            const currSessions = self.ApplicationSession.ApplicationModuleSessions
            let searchSession = undefined
            searchSession = _.first(_.filter(currSessions, (aModuleSession: IApplicationModuleSession) => (
                aModuleSession.ApplicationModule.wb_id == self.ApplicationModule.wb_id && aModuleSession.bIsSearchSession
            )))

            if (_.isUndefined(searchSession)) {
                searchSession = self.ApplicationSession.CreateModuleSession(_.toNumber(self.ApplicationModule.wb_id), { bIsSearchSession: true })
            }
            self.PlatformViewport.Renderer.setFocus(searchSession)
            self._ActionIsRunning = false
            self._OnActionCompletePromise = undefined
            self._notifyActionStatesChanged()
            return searchSession

            // Since we merge results into navigation list, then we can skip the unload check until the navigation results
            /*
            let bConfirm = await self._unloadBOCheck()
            if(!bConfirm) {
                self._ActionIsRunning = false
                self._OnActionCompletePromise  = undefined
                self._notifyActionStatesChanged()
                self._notifySessionChanged()
                return self
            }*/

            /*currentBO = self.BusinessObject
            try {
                let searchOpts: ISearchRendererOptions = self.getSearchOptions(opts)


                // Open Search Renderer..
                let arrIds = await self.PlatformViewport.SelectMany(self.ApplicationModule.executable.id_ui, searchOpts)

                if (_.isEmpty(arrIds)) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }

                self._bNavigationVisible = true

                let results: StandardSearchResults[] = []
                if (arrIds.length > 0) {
                    let aSearch: ISearch = self.ApplicationModule.executable.searchClass
                    let aSearchProfile: ISearchProfile = aSearch.searchProfiles[0]
                    let aSearchProvider: ISearchProvider = aSearchProfile.profileSearchProvider
                    let fieldName = aSearchProvider.keyFields.id
                    results = await SearchUtils.getStandardSearchResults(aSearch, {[fieldName]: {inq: arrIds}})
                }

                self._ActionIsRunning = false
                this._OnActionCompletePromise = undefined

                let moduleSession = self.ApplicationSession.CreateModuleSession(self.ApplicationModule.wb_id, {})
                self.ApplicationSession.PlatformViewport.Renderer.setFocus(moduleSession)

                if (results.length)
                    await self.setNavigationList(results, {bMerge: false})

                self._notifyActionStatesChanged()
            } catch (err) {
                self._ActionIsRunning = false
                this._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                throw err
            }*/

        })();

        self._notifyActionStatesChanged()
        return returnValue
    }


    get bRevertChangesVisible(): boolean {
        return this._bRevertChangesVisible &&
            ('onRevertChanges' in this)
    }

    get bRevertChangesEnabled(): boolean {
        return this.bRevertChangesVisible &&
            (!_.isEmpty(this._BusinessObject)) &&
            (!this._ActionIsRunning)
    }

    RevertChanges(): Promise<IApplicationModuleSession> {
        const self: any = this;

        if (this._ActionIsRunning)
            return ASync.error("Session Action already running.")

        if ('onRevertChanges' in self) {
            return self.onRevertChanges(this)
        } else {
            // View hasn't implemented UE
            return ASync.resolve(this)
        }
    }

    get bCopyVisible(): boolean {
        return this._bCopyVisible
    }

    get bCopyEnabled(): boolean {
        return this.bCopyVisible &&
            (!_.isEmpty(this._BusinessObject)) &&
            (!this._ActionIsRunning) &&
            ('Copy' in this._BusinessObject)
    }

    async Copy(options?: any): Promise<IApplicationModuleSession> {
        const self = this
        const selfany: any = this

        const OperationStart = new Date()

        const executable = self.ApplicationModule.executable

        self._ActionMessages.clearMessages()
        if (this._ActionIsRunning)
            throw new Error("Session Action already running.")

        self._ActionIsRunning = true
        const returnValue = this._OnActionCompletePromise = (async (): Promise<IApplicationModuleSession> => {

            let bContinue = true
            try {
                if ('beforeCopy' in self)
                    bContinue = await selfany.beforeCopy(self)

                const copyOptions: any = {}

                if (!bContinue) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self
                }

                if (_.isEmpty(self.BusinessObject)) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }

                let copyOptionsWizard = {}
                if (!_.isEmpty(executable.boCopyWizard)) {
                    // @ts-expect-error "Ignore"
                    const wizard = new executable.boCopyWizard.wizardFunction()
                    copyOptionsWizard = await wizard.copy(self.BusinessObject)
                }

                _.defaults(copyOptions, copyOptionsWizard, executable.boCopyOptions)

                self.BusinessObject.LoadProgressProvider.addListenerUtilComplete(self._ActionProgress)
                if (!_.isFunction(self.BusinessObject.Copy)) {
                    self._ActionMessages.addTextMessages("NoCopyFunction", MessageType.Error, "Copying Error:No copy function available for this module")
                    self._ActionIsRunning = false
                    throw new Error("No copy function available for this module")
                }


                self._ActionProgress.start("Copying...")
                let aBO = await self.BusinessObject.Copy(copyOptions)
                if (!_.isEmpty(executable.boAfterCopyWizard)) {
                    // @ts-expect-error "Ignore"
                    const wizard = new executable.boAfterCopyWizard.wizardFunction(aBO)
                    aBO = await wizard.open()
                }

                if (_.isEmpty(aBO)) {
                    self.BusinessObject.LoadProgressProvider.removeListener(self._ActionProgress)
                    self._ActionProgress.finish()
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self
                }

                // If caching BOs, open in same Session, otherwise, make new session
                let returnValue: IApplicationModuleSession = self
                if (self._bCacheBO) {
                    self._navigationItems.push({
                        id: aBO.Identity.id,
                        id_ui: aBO.Identity.id_ui + '',
                        text: aBO.Identity.name,
                        bo: aBO
                    })

                    await self.NavigateTo(aBO.Identity.id)

                } else {
                    // return the new session
                    returnValue = self.PlatformViewport.OpenBO(self.ApplicationModule.executable.id_ui, aBO)
                }
                self.BusinessObject.LoadProgressProvider.removeListener(self._ActionProgress)
                self._ActionProgress.finish()
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                self.logUsage("Copy", OperationStart, new Date(), false, aBO)
                return returnValue
            } catch (err) {

                self.BusinessObject.LoadProgressProvider.removeListener(self._ActionProgress)
                self._ActionProgress.finish()
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifyActionStatesChanged()
                throw err
            }

        })();

        self._notifyActionStatesChanged()
        return returnValue
    }


    //<editor-fold desc="Navigation Operations">


    get bNavigationVisible(): boolean {
        return this._bNavigationVisible && (this.ApplicationModule.placementPolicy != 'PaneContainer')
    }

    async setNavigationList(ids: number[] | string[] | StandardSearchResults[], options?: any): Promise<IApplicationModuleSession> {
        const self = this
        const opts = options || {}
        let arrNavigationList: INavigationItem[] = []
        const arrUnknownIds: (number | string)[] = []
        let dctNavigationItems: { [id: string]: INavigationItem } = {}

        if (opts.bMerge) {
            arrNavigationList = self._navigationItems || []
            dctNavigationItems = _.keyBy(arrNavigationList, (a) => a.id)
        }
        self._loadOption = opts

        // Create Navigation List Items
        _.forEach(ids, (anId) => {
            if (_.isNumber(anId) || _.isString(anId)) {
                if (!(anId in dctNavigationItems)) {
                    arrUnknownIds.push(anId)
                    dctNavigationItems[anId] = {
                        id: anId,
                        id_ui: anId + '',
                        text: `Object with Key (${anId})`,
                        bExists: true
                    }  // place holder
                    arrNavigationList.unshift(dctNavigationItems[anId])
                } else {
                    // Do nothing, already in list.
                }
            } else {  // Std List Item
                const aSearchItem: StandardSearchResults = <any>anId
                if (!_.isObjectLike(anId))
                    return

                if ('wb_id' in aSearchItem) {  // ignore it if it doesn't look like a search item
                    const key = aSearchItem.wb_id
                    if (key in dctNavigationItems) {
                        // Reset the names ?
                        const item = dctNavigationItems[key]
                        item.id_ui = aSearchItem.id_ui || item.id_ui || `Key (${key})`
                        item.text = aSearchItem.name || item.text || `Object with Key (${key})`
                        item.bExists = true
                    } else {
                        dctNavigationItems[key] = {
                            id: key,
                            id_ui: aSearchItem.id_ui,
                            text: aSearchItem.name,
                            bExists: true
                        }
                        arrNavigationList.unshift(dctNavigationItems[key])
                    }
                }
            }
        })

        if (!_.isEmpty(arrUnknownIds)) {
            // Use the search to populate the list
            const aSearch: ISearch = self.ApplicationModule.executable.searchClass
            if (aSearch) {
                const aSearchProfile: ISearchProfile = aSearch.searchProfiles[0]
                const aSearchProvider: ISearchProvider = aSearchProfile.profileSearchProvider
                const fieldName = aSearchProvider.keyFields.id
                const searchItems = await SearchUtils.getStandardSearchResults(aSearch, { [fieldName]: { inq: arrUnknownIds } })
                const dctSearchItems = _.keyBy(searchItems, (a) => a.wb_id)
                _.forEach(arrUnknownIds, (anId) => {
                    const si = dctSearchItems[anId]
                    const ni = dctNavigationItems[anId]

                    if (_.isEmpty(si) || _.isEmpty(ni))
                        return

                    ni.id_ui = si.id_ui
                    ni.text = si.name
                    ni.bExists = true
                })
            }
        }

        self._navigationItems = arrNavigationList

        if (opts.bSkipNavigation) {
            self._notifyActionStatesChanged()
            return
        }

        if (arrNavigationList.length) {
            self._bNavigationVisible = true
            const wbId = arrNavigationList[0].id
            await self.NavigateTo(wbId, opts)
        }

        self._notifyActionStatesChanged()

        return this
    }

    async clearNavigationList(): Promise<IApplicationModuleSession> {
        await this.setNavigationList([], {})
        return this
    }

    get NavigationItems(): INavigationItem[] {
        return this._navigationItems
    }

    get CurrentNavigationItem(): INavigationItem {
        return this._currentNavigationItem
    }


    get bNavigateNextEnabled(): boolean {

        if (!this._bNavigationVisible)
            return false

        if (this._ActionIsRunning)
            return false

        if (_.isEmpty(this._currentNavigationItem))
            return false

        const idx = _.indexOf(this._navigationItems, this._currentNavigationItem)
        if ((idx >= 0) && (idx < (this._navigationItems.length - 1)))
            return true

        return false
    }

    get bNavigatePreviousEnabled(): boolean {

        if (!this._bNavigationVisible)
            return false

        if (this._ActionIsRunning)
            return false

        if (_.isEmpty(this._currentNavigationItem))
            return false

        const idx = _.indexOf(this._navigationItems, this._currentNavigationItem)
        if (idx > 0)
            return true

        return false

    }


    async NavigateNext(): Promise<IApplicationModuleSession> {

        // Determine Next Id, then navigate to it.
        if (_.isEmpty(this._currentNavigationItem)) {
            console.log("No current item defined, can't navigate next")
            return this
        }

        const idx = _.indexOf(this._navigationItems, this._currentNavigationItem)
        if ((idx < 0) || idx >= (this._navigationItems.length - 1)) {
            console.log("Next Index not found, can't navigate next")
            return this
        }
        const id = this._navigationItems[idx + 1].id
        return await this.NavigateTo(id, this._loadOption)
    }

    async NavigatePrevious(): Promise<IApplicationModuleSession> {
        // Determine Previous Id, then navigate to it.
        if (_.isEmpty(this._currentNavigationItem)) {
            console.log("No current item defined, can't navigate previous")
            return this
        }

        const idx = _.indexOf(this._navigationItems, this._currentNavigationItem)
        if (idx <= 0) {
            console.log("Prev Index not found, can't navigate previous")
            return this
        }
        const id = this._navigationItems[idx - 1].id
        return await this.NavigateTo(id, this._loadOption)
    }

    NavigateTo(nextId: number | string, opts?: any): Promise<IApplicationModuleSession> {
        const self = this;
        const selfany: any = this

        const currentBO: IBusinessObject = self.BusinessObject
        if (self._bCacheBO && !_.isEmpty(currentBO)) {
            self.CurrentNavigationItem.bo = currentBO
            self.CurrentNavigationItem.bExists = true
        }

        if (this._ActionIsRunning)
            throw new Error("Session Action already running.")

        self._ActionIsRunning = true
        const returnValue = this._OnActionCompletePromise = (async (): Promise<IApplicationModuleSession> => {
            try {
                let nextBO = undefined

                let bContinue = true
                if ('beforeNavigate' in selfany)
                    bContinue = await selfany.beforeNavigate(self, nextId)

                if (!bContinue) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                }

                const bConfirm = await self._unloadBOCheck()
                if (!bConfirm) {
                    self._ActionIsRunning = false
                    self._OnActionCompletePromise = undefined
                    self._notifyActionStatesChanged()
                    return self;
                }
                // @ts-expect-error "Ignore"
                if (!_.isEmpty(currentBO) && !_.isEmpty(currentBO.RefCounter) && !self._bCacheBO) {
                    // @ts-expect-error "Ignore"
                    await currentBO.RefCounter.releaseReference()
                }

                const findNavItem = _.first(_.filter(self._navigationItems, anItem => anItem.id == nextId))

                if (!_.isEmpty(findNavItem) && !_.isEmpty(findNavItem?.bo)) {
                    const aNewBO = findNavItem.bo
                    aNewBO.LoadProgressProvider.removeListener(self._ActionProgress)
                    self._ActionMessages.clearMessages()
                    aNewBO.DeleteMessageProvider.clearMessages()
                    aNewBO.ValidateMessageProvider.clearMessages()

                    if (aNewBO.bIsLoaded) {
                        await self._replaceBO(aNewBO, true)
                    }
                } else
                    nextBO = await self._loadId(nextId, opts)

                const bNewBOLoaded = ((!_.isEmpty(self.BusinessObject)) &&
                    (currentBO !== self.BusinessObject))

                if (bNewBOLoaded) {
                    if ('afterNavigate' in selfany)
                        await selfany.afterNavigate(self)
                }

                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
            } catch (err) {
                console.log("Navigation Error: ", err)
            } finally {
                self._ActionIsRunning = false
                self._OnActionCompletePromise = undefined
                self._notifySessionChanged()
                self._notifyActionStatesChanged()
            }

            return self
        })();

        self._notifyActionStatesChanged()
        return returnValue
    }

    get bIdDialogVisible(): boolean {
        return this._bIdDialogVisible && !this.bIsSearchSession
    }

    get bIdDialogEnabled(): boolean {
        return this.bIdDialogVisible &&
            (!_.isEmpty(this._BusinessObject)) &&
            (!this._ActionIsRunning)
    }


    //</editor-fold>

    private async _replaceBO(aBusinessObject: IBusinessObject, bExists: boolean): Promise<void> {
        const self = this

        // Remove id listener
        const existingBO = self._BusinessObject
        if (existingBO) {
            existingBO.Identity.removeListener(self.Identity)
        }

        if (aBusinessObject) {

            aBusinessObject.Identity.addListener(self.Identity)
            self._Identity.setAll(aBusinessObject.Identity.id,
                aBusinessObject.Identity.id_ui,
                aBusinessObject.Identity.name)


            // Setup PolicyCollection
            // Use either local BO Security Polcies or Default App Ctx
            if (instanceOfIHaveSecurity(aBusinessObject)) {
                const aIHaveSecurity: IHaveSecurity = <any>aBusinessObject
                await aIHaveSecurity.OnIHaveSecurityLoaded
                if (_.isEmpty(aIHaveSecurity.PolicyCollection["BOis"])) {
                    const aBOisPolicy = new PolicyBOis(_.keys(self.ApplicationModule.editRoles), _.keys(self.ApplicationModule.viewRoles))
                    aIHaveSecurity.PolicyCollection.addPolicy("BOis", aBOisPolicy)
                }

                if (_.isEmpty(aIHaveSecurity.PolicyCollection["HasRole"])) {
                    const aHasRolePolicy = new PolicyHasRole()
                    await aHasRolePolicy.OnLoadComplete
                    aIHaveSecurity.PolicyCollection.addPolicy("HasRole", aHasRolePolicy)
                }
                self._PolicyCollection = aIHaveSecurity.PolicyCollection
                self._BusinessObject = aBusinessObject
            } else {
                // Create Policy Pool from
                const aPolicyCollection = new PolicyCollection()
                const aBOisPolicy = new PolicyBOis(_.keys(self.ApplicationModule.editRoles), _.keys(self.ApplicationModule.viewRoles))
                const aHasRolePolicy = new PolicyHasRole()
                aPolicyCollection.addPolicy("BOis", aBOisPolicy)
                await aHasRolePolicy.OnLoadComplete
                aPolicyCollection.addPolicy("HasRole", aHasRolePolicy)
                self._PolicyCollection = aPolicyCollection
                self._BusinessObject = aBusinessObject
            }

            // Set current navigation item
            const identity = aBusinessObject?.Identity
            if (identity) {
                const dctNavItems = _.keyBy(this._navigationItems, (a) => a.id)
                if (identity.id in dctNavItems) {
                    //--
                    self._currentNavigationItem = dctNavItems[identity.id]
                } else {
                    const ni: INavigationItem = {
                        id: identity.id,
                        id_ui: identity.id_ui + '',
                        text: identity.name,
                        bExists: bExists
                    }
                    self._navigationItems.push(ni)
                    self._currentNavigationItem = ni
                }
            }

        } else {
            self._currentNavigationItem = undefined

        }

        // @ts-expect-error "Ignore"
        if (!_.isEmpty(existingBO) && !self._bCacheBO && !_.isEmpty(existingBO.RefCounter)) {
            // @ts-expect-error "Ignore"
            const refCounter: IRefCounter = existingBO.RefCounter
            refCounter.releaseReference()
        }

    }

    private _evtOnIdentityChanged(identity: IIdentityProvider) {
        const self = this
        // Update the Navigation List id/ name
        if (_.isEmpty(identity))
            return

        const dctNavItems = _.keyBy(this._navigationItems, (a) => a.id)

        if (identity.id in dctNavItems) {
            const ni = dctNavItems[identity.id]
            ni.id_ui = identity.id_ui + ''
            ni.text = identity.name
        }

        self._notifyActionStatesChanged()

    }

    // doesn't check Action promise, call internally.. no notifications
    private async _loadId(id: number | string, options?: any): Promise<IBusinessObject> {
        const self = this;
        const selfany: any = this;

        const OperationStart = new Date()

        const aNewBO = self.createBO(options)
        if (_.isEmpty(aNewBO)) {
            return undefined;
        }

        const executable = self.ApplicationModule.executable

        // TODO:  why is this here?  what does this do ?
        if ('appSession' in executable.boLoadOptions) {
            options['appSession'] = self._ApplicationSession
        }

        aNewBO.LoadProgressProvider.addListenerUtilComplete(self._ActionProgress)


        options = _.merge(options, self.ApplicationSession.Application.renderOptions, executable.renderOptions, self.ApplicationModule.renderOptions)
        await aNewBO.Load(id, options)

        aNewBO.LoadProgressProvider.removeListener(self._ActionProgress)

        self._ActionMessages.clearMessages()
        aNewBO.DeleteMessageProvider.clearMessages()
        aNewBO.ValidateMessageProvider.clearMessages()

        if (aNewBO.bIsLoaded) {
            await self._replaceBO(aNewBO, true)
        } else {
            const errorTitle = "Failed to Load Business Object"
            const loadException = aNewBO?.LoadProgressProvider?.process_exception
            const loadExceptionMsg = loadException?.message || ""
            const loadExceptionStack = loadException?.stack || ""

            const errorDetails: string = errorTitle + "\n" + loadExceptionMsg + "\n\nStack:\n" + loadExceptionStack

            const loadErrorMsg: IMessage = {
                type: MessageType.Error,
                code: "loadBOError",
                title: errorTitle,
                subtitle: loadExceptionMsg || errorDetails,
                description: errorDetails,
                details: errorDetails
            }

            self._ActionMessages.addMessage(loadErrorMsg);
            self._ActionProgress.exception(errorDetails);
            throw new Error(aNewBO.LoadProgressProvider.process_exception)
        }

        return aNewBO
    }

    private _notifyActionStatesChanged(): void {
        this._ActionStateChanged.next(this)
    }

    private _notifyActionStatesCompleted(actionName: string): void {
        this._OnActionComplete.next({ Session: this, Action: actionName })

        const platformSession = this.ApplicationSession.PlatformViewport.PlatformSession
        platformSession.OnOperation.next({
            platformSession,
            operationName: `ApplicationModuleSession${actionName}`,
            applicationSession: this.ApplicationSession,
            applicationModuleSession: this
        })
    }

    private _notifySessionChanged(): void {
        this._SessionChanged.next(this)
    }

    private createBO(options?: any): IBusinessObject {
        const executable = this.ApplicationModule.executable
        const createOptions = _.clone(executable.boCreateOptions || {})
        _.defaults(createOptions, options || {})
        return new executable.boClass(createOptions)
    }

    // returns true if unloaded
    private async _unloadBOCheck(sMessage?: string): Promise<boolean> {
        const self = this


        if (_.isEmpty(self.BusinessObject) || self.BusinessObject?.bIsDeleted || !_.isFunction(self.BusinessObject?.Changes))
            return true

        let bSkipChangeSet = false
        if (_.isFunction(self.BusinessObject.beforeClose)) {
            bSkipChangeSet = self.BusinessObject.beforeClose()
        }

        if (bSkipChangeSet)
            return true

        const changeSet = self.BusinessObject.Changes({ changeCountThreshold: 5, bIncludeFieldDetails: true })

        if (_.isEmpty(changeSet) || !changeSet?.bHasChanges)
            return true

        const boNameId = `${self.BusinessObject?.Identity?.name} (${self.BusinessObject?.Identity?.id_ui})`
        const itemList = _.map(changeSet.Changes, (a) => a.Message)
        const confirmMessage = sMessage || "Changes Detected. Would you like to save before navigating away?"
        try {
            const bConfirm = !await self.PlatformViewport.Renderer.messageBoxRenderer.Confirm(confirmMessage, {
                title: boNameId,
                listItems: itemList,
                listItemHeaderText: "Change Details",
                sYesTextOvrd: "Yes, Go Back",
                sNoTextOvrd: "No, Don't Save"
            })
            if (bConfirm && !changeSet.bMainRecordExists) {
                if (!self._bCacheBO) {
                    // Remove unsaved, new record from navigation list
                    const id = self.BusinessObject.Identity.id
                    _.remove(self._navigationItems, (item) => item.id == id)
                }
                self._currentNavigationItem = undefined
            }
            return bConfirm

        } catch (err) {
            return false
        }

    }


    get bReloadBOVisible(): boolean {
        return this._bReloadBOVisible
    }

    get bReloadBOEnabled(): boolean {
        return this.bIsObjectLoaded
    }

    async ReloadBO(): Promise<IBusinessObject> {
        const self = this;
        const currentBO = self._BusinessObject

        const bConfirm = await self._unloadBOCheck()
        if (!bConfirm) {
            self._OnActionCompletePromise = undefined
            self._notifyActionStatesChanged()
            return currentBO;
        }

        if (_.isFunction(currentBO.Reload)) {
            const aBO = await currentBO.Reload()
            if (!aBO.bIsLoaded) {
                //Hmm.. crap.
                console.log("AppSession Reload BO Failed")
            }
            return currentBO
        } else {
            const aNewBO = self.createBO()
            const dbContext = new PAdbContext()
            const aBO = await aNewBO.Load(currentBO.Identity.id, { dbContext: dbContext })

            if (aBO.bIsLoaded)
                await self.SetBO(aBO)
        }

    }


    get bCreateNoteVisible(): boolean {
        return this._bCreateNoteVisible
    }

    get bCreateNoteEnabled(): boolean {
        return this.bIsObjectLoaded
    }

    CreateNote(): Promise<NoteBO> {
        const self = this;

        return new Promise<NoteBO>((resolve, error) => {

            const usrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
            const usrID = usrContext.User.WB_AUTHUSR_ID
            const aDbContext = new PAdbContext({});
            const aNoteBO = new NoteBO({ dbContext: aDbContext, bEagerLoadAspectInterfaces: true });
            aNoteBO.LoadNew({ dbContext: aDbContext })
                .then((aNoteBO: NoteBO) => {
                    if (aNoteBO.bIsLoaded) {
                        aNoteBO.WB_Note.cif.WB_APP_ID = <any>self.Identity.id
                        aNoteBO.WB_Note.cif.APP_TYPE = self.ApplicationModule.executable.id_ui
                        aNoteBO.WB_Note.cif.WB_AUTHUSR_ID = usrID
                        return aNoteBO.addNoteUser(usrID);
                    } else {
                        console.log('Note BO creation failed')
                        error('Note BO creation failed')
                    }
                })
                .then(function(result: NoteBO) {
                    resolve(aNoteBO)
                })
                .catch((err) => {
                    console.log('Note BO creation failed:')
                    console.log(err);
                    error(err)
                });
        });
    }

    get bFileListVisible(): boolean {
        return this._bFileListVisible
    }

    get bFileListEnabled(): boolean {
        if (_.isEmpty(this.BusinessObject))
            return true
        return this.bIsObjectLoaded
    }

}
